// SignInlineDocument — the document itself, on the public sign page, below the
// who-has-signed status.
//
//   Miles, 2026-09-24: "the status page of who has signed should just display
//   the lease rather than making someone click to view. click to download is a
//   fine additional step though."
//
// Rendered by SignPage in the states that show the signing status AND offer a
// download (awaiting, signed, executed — wherever `download.available`), for
// every document type the page offers a download for. The Download button in
// the status card stays exactly where it was; this is the view, not a
// replacement for the file.
//
// W-9: never. The server reports download.available=false for a W-9 (its PDF
// carries the owner's TIN) and refuses the inline bytes as well, so a W-9 page
// renders neither this block nor a download.
//
// Staff (a WHP role or a welcomehps.com signer — server-gated): the lease
// Review summary sits directly above the document, "the full header version of
// the lease with the lease below it". Residents' views never carry the summary.
//
// Voice: neutral on purpose — the same block is read by residents, co-signers,
// owners and WHP staff, so it names the document rather than addressing "your".

import { useEffect, useState } from 'react';
import { signDocumentUrl, type SignView } from '@/lib/signClient';
import { PdfPages } from '@/components/PdfPages';
import { ReviewSummaryCard } from '@/components/ReviewSummaryCard';

/**
 * The same current-state document the Download button opens, as BYTES streamed
 * inline (`?inline=1`): the page renders it itself, which a 302 to a
 * cross-origin S3/DocuSeal URL would not allow. Same access checks server-side;
 * a W-9 is refused there exactly as its download is. No Authorization header —
 * the uuid in the path is the only credential. Built on signDocumentUrl so the
 * two can never point at different documents.
 */
async function fetchSignDocumentBytes(uuid: string, signal: AbortSignal): Promise<ArrayBuffer> {
  const res = await fetch(`${signDocumentUrl(uuid)}?inline=1`, { signal });
  if (!res.ok) throw new Error(`document fetch failed (${res.status})`);
  return res.arrayBuffer();
}

// Copy approved by Miles 2026-09-25.
const IN_PROGRESS_NOTE =
  'This is the document as of when you opened this page. Refresh to see new signatures.';

const CANNOT_SHOW =
  'The document can’t be shown on this page right now. The download button above opens it.';

export function SignInlineDocument({
  uuid,
  label,
  executed,
  refreshKey,
}: {
  uuid: string;
  /** The page's document label ("Lease", "Payment Plan", a manual doc's name). */
  label: string;
  /** Fully executed → "the fully executed lease"; otherwise "as it stands now". */
  executed: boolean;
  /** Changes whenever the signing state moves, so the partial copy re-fetches
   *  with the newest marks (e.g. the webhook landing after the signer's own
   *  signature). */
  refreshKey: string;
}): React.JSX.Element {
  const [data, setData] = useState<ArrayBuffer | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setData(null);
    setError(false);
    fetchSignDocumentBytes(uuid, ctrl.signal)
      .then((bytes) => {
        if (!ctrl.signal.aborted) setData(bytes);
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setError(true);
      });
    return () => ctrl.abort();
  }, [uuid, refreshKey]);

  const noun = label.toLowerCase();
  return (
    <section className="card" style={{ marginTop: 'var(--space-5)' }} data-section="inline-document">
      <h2
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'var(--text-lg)',
          fontWeight: 400,
          color: 'var(--fg)',
          margin: 0,
        }}
      >
        {executed ? `The fully executed ${noun}` : `The ${noun} as it stands now`}
      </h2>
      {executed ? null : (
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--fg-muted)', fontSize: 'var(--text-sm)' }}>
          {/* Accurate on purpose: awaiting/signed
              pages do not poll, so the copy fetched on open is what shows. */}
          {IN_PROGRESS_NOTE}
        </p>
      )}
      <div style={{ marginTop: 'var(--space-4)' }}>
        {error ? (
          <p style={{ color: 'var(--fg-muted)', margin: 0 }}>{CANNOT_SHOW}</p>
        ) : data ? (
          <PdfPages data={data} title={label} onError={() => setError(true)} />
        ) : (
          <p style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
            Loading the document…
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Everything the sign page shows BELOW the status card in a non-signing state:
 * the staff Review summary (only when the server sent one) and then the
 * document inline (only when a download exists). Renders nothing for a
 * resident on a document without a download, and nothing at all for a W-9.
 */
export function SignDocumentBelowStatus({
  uuid,
  view,
  label,
}: {
  uuid: string;
  view: Pick<SignView, 'state' | 'parties' | 'download' | 'reviewSummary'>;
  label: string;
}): React.JSX.Element | null {
  const showDocument = view.download.available;
  if (!view.reviewSummary && !showDocument) return null;
  const signedCount = view.parties.filter((p) => p.status === 'signed').length;
  return (
    <>
      {view.reviewSummary ? (
        <ReviewSummaryCard
          summary={view.reviewSummary}
          style={{ marginTop: 'var(--space-5)', marginBottom: 0 }}
        />
      ) : null}
      {showDocument ? (
        <SignInlineDocument
          uuid={uuid}
          label={label}
          executed={view.state === 'executed'}
          refreshKey={`${view.state}:${signedCount}`}
        />
      ) : null}
    </>
  );
}
